import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Checkbox,
  Stack,
  VStack,
  HStack,
  Input,
  InputGroup,
  Badge,
  InputLeftElement,
  Text,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { Header } from "build-ts/metadata";

interface MetricsSelectionProps {
  metricList: Header[];
  selectedMetricHeader: Header[];
  onMetricChange: (event: React.ChangeEvent<HTMLInputElement>, metricId: string) => void;
  onSelectAll: () => void;
  onClearSelections: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  width?: string;
}

const MetricSelection: React.FC<MetricsSelectionProps> = ({
  metricList,
  selectedMetricHeader,
  onMetricChange,
  onSelectAll,
  onClearSelections,
  isLoading = false,
  isDisabled = false,
  width = "300px",
  }) => {
    const [metricSearchTerm, setMetricSearchTerm] = useState<string>("");

  // Filter metrics based on search term
  const filteredMetrics = useMemo(() => {
    if (!metricSearchTerm) return metricList;

    return metricList.filter((metric) =>
      metric.name?.toLowerCase().includes(metricSearchTerm.toLowerCase())
    );
  }, [metricList, metricSearchTerm]);

  // Handle search input change
  const handleMetricSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMetricSearchTerm(e.target.value);
  };

  // Check if all filtered metrics are selected
  const areAllFilteredMetricsSelected = useMemo(() => {
    if (filteredMetrics?.length === 0) return false;

    const result = filteredMetrics?.every((metric) =>
      selectedMetricHeader?.some((h) => h.id === metric.id)
    );

    return result;
  }, [filteredMetrics, selectedMetricHeader]);

  return (
    <Box width={width}>
      <VStack spacing={4} align="stretch">
        {/* Search filter for metrics */}
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search metrics..."
            size="sm"
            variant="filled"
            value={metricSearchTerm}
            onChange={handleMetricSearch}
            _focus={{
              borderColor: "purple.500",
              boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)"
            }}
          />
        </InputGroup>

        {/* Select All checkbox */}
        <Checkbox
          isChecked={areAllFilteredMetricsSelected}
          onChange={onSelectAll}
          fontWeight="semibold"
          size="sm"
          isDisabled={filteredMetrics?.length === 0 || isLoading}
          colorScheme="purple"
        >
          {areAllFilteredMetricsSelected ? "Deselect All" : "Select All"}
          {metricSearchTerm ? " Filtered" : ""}
        </Checkbox>

        {/* Metrics list container */}
        <Box 
          maxH="300px" 
          overflowY="auto" 
          border="1px" 
          borderColor="gray.200" 
          borderRadius="md" 
          p={3}
          bg="white"
        >
          <Stack spacing={2}>
            {filteredMetrics?.map((metric) => {
              const isChecked = selectedMetricHeader?.some(
                (h) => h.id === metric.id
              );
              
              return (
                <Checkbox
                  key={metric.id}
                  isChecked={isChecked}
                  onChange={(e) => onMetricChange(e, metric.id || "")}
                  isDisabled={isLoading || isDisabled}
                  colorScheme="purple"
                >
                  {metric.name || "Unnamed Metric"}
                </Checkbox>
              );
            })}
            {filteredMetrics?.length === 0 && (
              <Text fontSize="sm" color="gray.500" p={2} textAlign="center">
                No metrics found matching "{metricSearchTerm}"
              </Text>
            )}
          </Stack>
        </Box>

        {/* Selection summary and clear button */}
        {selectedMetricHeader?.length > 0 && (
          <HStack justify="space-between" align="center">
            <Badge colorScheme="purple" borderRadius="full" px="3" py="1">
              {selectedMetricHeader.length} selected
            </Badge>
            <Button
              size="sm"
              colorScheme="red"
              variant="outline"
              onClick={onClearSelections}
              isDisabled={isLoading}
            >
              Clear All
            </Button>
          </HStack>
        )}
      </VStack>
    </Box>
  );
};

export default MetricSelection; 